/**
 * doctors.service.ts
 *
 * Thin wrapper over GET /v5/doctors, scoped by the X-Target-Company header
 * injected by apiClient. Used by the QA editor modal's Medical Provider select
 * and by any future feature that needs the account's doctor list.
 *
 * The server returns snake_case fields (first_name / last_name) via
 * DoctorsController::index; we re-map to camelCase for the V5 UI.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface Doctor {
  doctorID: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  suffix?: string;
  active: boolean;
}

interface DoctorApiRow {
  doctorID: number;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  suffix?: string;
  active?: boolean;
}

export interface ListDoctorsFilters {
  /** Pass `false` for ONLY inactive doctors. Omit (or `true`) for active-only. */
  active?: boolean;
  /**
   * Doctor ids the caller's record already holds. They come back whatever their
   * state — flagged `active: false` — so an assigned-but-departed physician still
   * renders in the field that stores them. See lib/staffOptions.
   */
  includeIds?: number[];
  /**
   * Everyone who ever held the role, de-activated people included (flagged
   * `active: false`). For FILTER lists, where a departed physician must stay
   * selectable or their records become unfindable — not for assignment selectors.
   */
  includeInactive?: boolean;
}

export function listDoctors(filters: ListDoctorsFilters = {}): Promise<Doctor[]> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.active !== undefined) params.active = filters.active;
  if (filters.includeIds?.length) params.includeIds = filters.includeIds.join(',');
  if (filters.includeInactive) params.includeInactive = 1;

  return apiClient
    .get<ApiResponse<DoctorApiRow[]>>('/doctors', { params })
    .then((r) =>
      (r.data.data ?? []).map((d) => ({
        doctorID: d.doctorID,
        firstName: d.first_name ?? '',
        lastName: d.last_name ?? '',
        middleName: d.middle_name ?? '',
        suffix: d.suffix ?? '',
        active: !!d.active,
      })),
    );
}

export function formatDoctorName(d: Pick<Doctor, 'firstName' | 'lastName'>): string {
  return `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim();
}
